import * as common from '../../common';
import { WebTools } from '../../WebTools';
import { WorkerPool } from '../workerPool';
import { db, clinic_user_retinal_images, filepath_to_png } from '../../db';
import {
  GetParticipantStudyEyeRequest,
  GetParticipantStudyEyeResponse,
  RecordStudyEyeUploadInput,
  RecordStudyEyeUploadRequest,
  RecordStudyEyeUploadResponse,
} from '../types/studyEvents';

// Modality is the raw detection from device metadata
export type E2EModality = 
  | 'OCT' 
  | 'FAF' 
  | 'IR' 
  | 'ICGA' 
  | 'FA' 
  | 'MULTICOLOR' 
  | 'REDFREE' 
  | 'FUNDUS' 
  | 'UNKNOWN';

// Suggested type refines modality with location info (ONH suffix)
export type E2ESuggestedType =
  | 'OCT' | 'OCTONH'
  | 'FAF' | 'FAFONH'
  | 'IR' | 'IRONH'
  | 'ICGA' | 'ICGAONH'
  | 'FA' | 'FAONH'
  | 'MULTICOLOR' | 'MULTICOLORONH'
  | 'REDFREE' | 'REDFREEONH'
  | 'FUNDUS' | 'FUNDUSONH'
  | 'UNKNOWN';

export type E2EManifest = {
  version: string;
  path_format?: string;
  source_file?: string;
  processed_at?: string;
  capture_date: string;
  capture_datetime: string;
  patient_id: string;
  study_id: string;
  device?: { manufacturer: string; name?: string; type?: string };
  facility?: string;
  timezone?: string;
  patient_reference?: string;
  secondary_id?: string;
  series: E2ESeries[];
  summary: {
    total_series: number;
    by_eye: { OD: number; OS: number };
    by_type: Partial<Record<E2ESuggestedType, number>>;
  };
};

export type E2ESeries = {
  series_id: string;
  eye: 'OD' | 'OS' | 'OU';
  capture_date: string;
  capture_datetime?: string;
  capture_datetime_end?: string;
  scan_duration_seconds?: number;
  modality: E2EModality;
  suggested_type: E2ESuggestedType;
  enface_mode?: string;
  enface_modes?: string[];
  location_hint?: 'macula' | 'optic_disc' | 'temporal_field';
  
  // OCT-specific fields
  slice_count?: number;
  needs_interpolation?: boolean;
  scan_bounds_deg?: { x: [number, number]; y: [number, number] };
  quality_mean?: number;
  art_frames?: number[];
  
  // Dimensions for various image types
  dimensions?: {
    bscan?: { width: number; height: number };
    ir?: { width: number; height: number };
    combined?: { width: number; height: number };
    fundus?: { width: number; height: number };
  };
  
  // Path arrays and counts
  ir_paths?: string[];
  ir_count?: number;
  bscan_paths?: string[];
  bscan_count?: number;
  combined_paths?: string[];
  combined_count?: number;
  fundus_paths?: string[];
  fundus_count?: number;
};

class ImageSyncService
{
  cachedAuth: VividAuthWithPatient;

  private static readonly VIVID_URL = 'https://www.seevividly.com/api_localizer/';
  private static readonly VIVIDAPI_SYNC = 'sync_retinal_images';
  private static readonly VIVIDAPI_UPLOAD = 'upload_retinal_images';
  private static readonly VIVIDAPI_DELETE = 'delete_retinal_images';
  private static readonly VIVIDAPI_GET_PARTICIPANT_STUDY_EYE = 'get_participant_study_eye';
  private static readonly VIVIDAPI_RECORD_STUDY_EYE_UPLOAD = 'record_study_eye_upload';
  private static readonly LAMBDA_URL = 'https://upload-images.seevividly.com/'; // now points to PROD lambda alias, was https://43vb5jhsi6ewen44757wphw5ym0xetia.lambda-url.us-east-1.on.aws/ which points to $LATEST lambda
  private _subdirs: ImageTree<string>;
  private _presignedGet: S3PresignedGetInfo;
  private _presignedGetTime: number;
  private _presignedUpload: S3PresignedUploadInfo;
  private _presignedUploadTime: number;

  private _syncedRows: clinic_user_retinal_images[];
  private _collator = new Intl.Collator('en');

  get subdirs() { return this._subdirs; }

  async syncVivid(timeout: number, cancel: AbortSignal) : Promise<any>
  {
    const r = await this.queryVivid(timeout, cancel);
    const myRows = await db.metaWhereUser()
      .sortBy('filepath');
    const myMap = common.buildMap(myRows, r => r.filepath);
    const myOld = myRows.length < 1 ? '0' : common.selectMin(myRows, v => v.timestamp).timestamp;
    const svOld = r.rows.length < 1 ? '0' : common.selectMin(r.rows, v => v.timestamp).timestamp;
    const willClear = (r.delete && myRows.some(row => row.timestamp <= r.delete))
      || (!r.delete && r.rows.length < 1)
      || (!r.delete && r.rows.length > 0 && myRows.length > 0 && svOld > myOld);

    if (willClear && myRows.length > 0) {
      if (r.delete && myRows.some(row => row.timestamp > r.delete))
        console.warn('Unexpected: local rows to delete, but some others are newer');
      const myKeys = await db.metaWhereUser().primaryKeys();
      await db.clinic_user_retinal_images.bulkDelete(myKeys);
      await db.filepath_to_png.bulkDelete(myKeys);
      myMap.clear();
    }
    const toPut: clinic_user_retinal_images[] = [];
    const staleChecksums: string[] = [];
    for (const row of r.rows) {
      const mine = myMap.get(row.filepath);
      if (!mine || mine.timestamp < row.timestamp)
        toPut.push(row);
      if (mine && mine.sha1 !== row.sha1)
        staleChecksums.push(row.filepath);
    }
    if (staleChecksums.length > 0) {
      await db.filepath_to_png.bulkDelete(staleChecksums);
    }
    if (toPut.length > 0)
      await db.clinic_user_retinal_images.bulkPut(toPut);
    return {
      all: r.rows,
      fresh: [...staleChecksums, ...toPut.map(r => r.filepath)],
    };
  }

  async queryVivid(timeout: number, cancel: AbortSignal) : Promise<VividSyncResponse>
  {
    const body = {
      ...this.cachedAuth,
    };
    // @ts-ignore
    const signal = AbortSignal.any([cancel, AbortSignal.timeout(timeout)]);
    const r = await WebTools.apiPost<VividSyncResponse>(ImageSyncService.VIVID_URL + ImageSyncService.VIVIDAPI_SYNC, body, signal);
    r.rows = r.rows.map(r => db.metaFrom(r));
    r.rows.sort((a, b) => this._collator.compare(a.filepath, b.filepath));
    return r;
  }

  async uploadVivid(filenames: string[], timeout: number, cancel: AbortSignal)
  {
    const body = {
      ...this.cachedAuth,
      files: []
    };
    const myRows = await db.metaWhereUser()
      .sortBy('filepath');
    const uploadRows = myRows.filter(row => filenames.includes(row.filepath));
    body.files = uploadRows;
    // @ts-ignore
    const signal = AbortSignal.any([cancel, AbortSignal.timeout(timeout)]);
    const results = await WebTools.apiPost<VividUploadResponse>(ImageSyncService.VIVID_URL + ImageSyncService.VIVIDAPI_UPLOAD, body, signal);
    console.info(`Upload resulted in ${results.write.length} written, ${results.skip.length} skipped, ${results.error.length} errors`);
    return results;
  }

  async deleteVivid(timeout: number, cancel: AbortSignal)
  {
    const body = {
      ...this.cachedAuth,
      time: Date.now() / 1000
    };
    // @ts-ignore
    const signal = AbortSignal.any([cancel, AbortSignal.timeout(timeout)]);
    const result = await WebTools.apiPost<any>(ImageSyncService.VIVID_URL + ImageSyncService.VIVIDAPI_DELETE, body, signal);
    return result;
  }

  async getParticipantStudyEye(historyLimit?: number): Promise<GetParticipantStudyEyeResponse>
  {
    const body: GetParticipantStudyEyeRequest = {
      ...this.cachedAuth,
      ...(historyLimit === undefined ? {} : { history_limit: historyLimit }),
    };
    return WebTools.apiPost<GetParticipantStudyEyeResponse>(
      ImageSyncService.VIVID_URL + ImageSyncService.VIVIDAPI_GET_PARTICIPANT_STUDY_EYE,
      body
    );
  }

  async recordStudyEyeUpload(request: RecordStudyEyeUploadInput): Promise<RecordStudyEyeUploadResponse>
  {
    const body: RecordStudyEyeUploadRequest = {
      ...this.cachedAuth,
      ...request,
    };
    return WebTools.apiPost<RecordStudyEyeUploadResponse>(
      ImageSyncService.VIVID_URL + ImageSyncService.VIVIDAPI_RECORD_STUDY_EYE_UPLOAD,
      body
    );
  }

  async requestSubdirs() : Promise<ImageTree<string>>
  {
    // if (this._subdirs !== undefined)
    //   return this._subdirs;
    this._subdirs = (await WebTools.apiPost<SubdirResponse>(ImageSyncService.LAMBDA_URL, { method: 'subdir' })).subdir_info;
    return this._subdirs;
  }

  async requestPresignedGet(timeout: number, cancel: AbortSignal) : Promise<S3PresignedGetInfo>
  {
    // if (this._presignedGetTime && Date.now() - this._presignedGetTime < 3000)
    //   return this._presignedGet;
    const body = {
      ...this.cachedAuth,
      method: 'sync',
      files: {}
    };
    const myRows = await db.metaWhereUser()
      .sortBy('filepath');
    // build files arg like ImageTree of filename:checksum
    for (const row of myRows) {
      const pngExists = await db.filepath_to_png
        .where('filepath')
        .equals(row.filepath)
        .count() > 0;
      if (!pngExists)
        continue;
      body.files[row.filepath.split('/').pop()] = row.sha1;
    }
    // @ts-ignore
    const signal = AbortSignal.any([cancel, AbortSignal.timeout(timeout)]);
    this._presignedGet = await WebTools.apiPost<S3PresignedGetInfo>(ImageSyncService.LAMBDA_URL, body, signal);
    this._presignedGetTime = Date.now();
    return this._presignedGet;
  }

  async requestPresignedUpload(timeout: number, cancel: AbortSignal) : Promise<S3PresignedUploadInfo>
  {
    if (this._presignedUploadTime && Date.now() - this._presignedUploadTime < 3600000)
      return this._presignedUpload;
    const body = {
      ...this.cachedAuth,
      method: 'upload'
    };
    // @ts-ignore
    const signal = AbortSignal.any([cancel, AbortSignal.timeout(timeout)]);
    this._presignedUpload = (await WebTools.apiPost<PresignedUploadResponse>(ImageSyncService.LAMBDA_URL, body, signal)).presigned_info;
    this._presignedUploadTime = Date.now();
    return this._presignedUpload;
  }

  async requestPresignedUploadE2E(timeout: number, cancel: AbortSignal): Promise<S3PresignedUploadInfo>
  {
    const body = {
      ...this.cachedAuth,
      method: 'upload',
      file_type: 'e2e'  // This tells Lambda to allow non-image content types
    };
    // @ts-ignore
    const signal = AbortSignal.any([cancel, AbortSignal.timeout(timeout)]);
    const response = await WebTools.apiPost<PresignedUploadResponse>(ImageSyncService.LAMBDA_URL, body, signal);
    return response.presigned_info;
  }

  // Authoritative, collision-safe E2E upload target. The server assigns and
  // PINS the storage key (based on filename + content sha1), so a same-named
  // different-content file can never overwrite an existing one. Returns
  // status 'exists' when identical content is already present (skip upload).
  async requestUploadUrlE2E(filename: string, sha1: string, size: number, cancel: AbortSignal): Promise<E2EUploadUrlResponse>
  {
    const body = {
      ...this.cachedAuth,
      method: 'upload_url',
      file_type: 'e2e',
      filename,
      sha1,
      size,
    };
    // @ts-ignore
    const signal = AbortSignal.any([cancel, AbortSignal.timeout(30000)]);
    return await WebTools.apiPost<E2EUploadUrlResponse>(ImageSyncService.LAMBDA_URL, body, signal);
  }

  async downloadS3(presignedInfo: S3PresignedGetInfo, timeout: number, onEach: () => void) : Promise<any[]>
  {
    const tasks = [];
    for (let [filepath, url] of Object.entries(presignedInfo)) {
      if (!filepath.startsWith(this.cachedAuth.patient_id.toString()))
        filepath = this.cachedAuth.patient_id + '/' + filepath;
      tasks.push({
        method: 'downloadS3',
        args: {
          patientID: this.cachedAuth.patient_id,
          path: filepath,
          url: url,
          timeout: timeout,
        },
      });
    }
    const pool = new WorkerPool('imageSync', 6);
    const downloads = new Map<number, Promise<any>>();
    const failed = [];
    let resolveEscape;
    const escape = new Promise((resolve) => {
      resolveEscape = resolve;
    });
    const onComplete = (v, i) => {
      downloads.delete(i);
      onEach();
    };
    const onError = (e, i) => {
      console.warn(e.name, e.message);
      downloads.delete(i);
      failed.push({
        filepath: tasks[i].args.path,
        errorName: e.name,
      });
      pool.terminate();
      resolveEscape();
    };
    for (let i = 0; i < tasks.length; i++) {
      if (failed.length > 0)
        break;
      const p = pool.addTask(tasks[i])
        .then(v => onComplete(v, i))
        .catch(e => onError(e, i));
      downloads.set(i, p);
    }
    await Promise.race([
      escape,
      Promise.all([...downloads.values()]),
    ]);
    return failed;
  }

  async uploadS3(presignedInfo: S3PresignedUploadInfo, paths: string[], timeout: number, onEach: () => void) : Promise<any[]>
  {
    await this.requestSubdirs();
    const metaRows = await db.clinic_user_retinal_images
      .where('filepath').anyOf(paths)
      .sortBy('filepath');
    const pngCt = await db.filepath_to_png
      .where('filepath').anyOf(paths)
      .count();
    if (metaRows.length !== paths.length || pngCt !== paths.length)
      throw new Error('File list inconsistent with database');

    const tasks = metaRows.map(row => {
      const baseType = row.type.replace(' Properties', '');
      const subdir = this._subdirs[baseType][row.eye];
      return {
        method: 'uploadS3',
        args: {
          path: row.filepath,
          subdir: subdir,
          pre: presignedInfo,
          timeout: timeout,
        },
      };
    });
    const pool = new WorkerPool('imageSync', 2);
    const uploads = new Map<number, Promise<any>>();
    const failed = [];
    const onComplete = (v, i) => {
      uploads.delete(i);
      onEach();
    };
    const onError = (e, i) => {
      console.warn(e.name, e.message);
      failed.push({
        filepath: tasks[i].args.path,
        errorName: e.name,
      });
    };
    for (let i = 0; i < tasks.length; i++) {
      const p = pool.addTask(tasks[i])
        .then(v => onComplete(v, i))
        .catch(e => onError(e, i));
      uploads.set(i, p);
    }
    await Promise.all([...uploads.values()]);
    return failed;
  }
  
  async deleteS3(timeout: number, cancel: AbortSignal) : Promise<any>
  {
    const body = {
      ...this.cachedAuth,
      method: 'delete'
    };
    // @ts-ignore
    const signal = AbortSignal.any([cancel, AbortSignal.timeout(timeout)]);
    const r = await WebTools.apiPost<S3DeleteResponse>(ImageSyncService.LAMBDA_URL, body, signal);
    if (r.errors.length > 0)
      throw new Error('Failed to delete from S3');
    return r;
  }


  //E2E
  async requestE2EUpload(items, abortSignal: AbortSignal) {
    const body = {
      ...this.cachedAuth,
      method: 'propose_upload',
      files: items.map(item => ({
        filename: item.file.name,
        size: item.file.size,
        sha1: item.sha1,
      })),
    };
    const r = await WebTools.apiPost<any>(ImageSyncService.LAMBDA_URL, body, abortSignal);
    // uploadType: string;
    return r.files as {
      originalFilename: string;
      filename: string;
      destPath: string;
      shouldUpload: boolean;
      reason: string;
    }[];
  }

  async checkProcessed(filenames?: string[]) {
    const body: any = {
      ...this.cachedAuth,
      method: 'poll_e2e',
    };
    // Only include e2e_filenames if provided and non-empty
    if (filenames && filenames.length > 0) {
      body.e2e_filenames = filenames;
    }
    const r = await WebTools.apiPost<any>(ImageSyncService.LAMBDA_URL, body);
    return r.files as {
      filename: string;
      status: string;
      seriesCount?: number;
      uploaded_at?: string;
      size?: number;
      // Content sha1 (echoed from the .E2E object's x-amz-meta-sha1). Lets the
      // resume/reload path recover upload provenance; absent on older objects.
      sha1?: string;
    }[];
  }
  
  async getProcessed(filenames?: string[]) {
    const body: any = {
      ...this.cachedAuth,
      method: 'e2e_output_info',
    };
    // Only include e2e_filenames if provided and non-empty
    if (filenames && filenames.length > 0) {
      body.e2e_filenames = filenames;
    }
    const r = await WebTools.apiPost<any>(ImageSyncService.LAMBDA_URL, body);
    return r.outputs as {
      filename: string;
      outputPath: string;
      manifest?: E2EManifest;
      error?: string;
    }[];
  }

  async viewProcessed(paths: string[]) {
    const body = {
      ...this.cachedAuth,
      method: 'view_urls',
      paths: paths,
    };
    const r = await WebTools.apiPost<any>(ImageSyncService.LAMBDA_URL, body);
    return r.urls as {
      path: string;
      url: string;
      expiresIn: number;
    }[];
  }

  async selectE2EOutput(
    selections: {
      OD?: { OCT?: string; FAF?: string; FAFONH?: string };
      OS?: { OCT?: string; FAF?: string; FAFONH?: string };
    },
    options?: { interpolate_oct?: boolean },
    progressId?: string
  ) {
    const body = {
      ...this.cachedAuth,
      method: 'select_e2e_output',
      selections,
      options,
      ...(progressId ? { progress_id: progressId } : {}),
    };
    const r = await WebTools.apiPost<{
      success: boolean;
      capture_date: string;
      created: {
        OCT: { OD: string[]; OS: string[] };
        FAF: { OD: string[]; OS: string[] };
        FAFONH: { OD: string[]; OS: string[] };
      };
      db_result: { write: string[]; skip: string[]; error: string[] };
      file_count: number;
    }>(ImageSyncService.LAMBDA_URL, body);
    return r;
  }

  // Poll VVP-package creation progress written by select_e2e_output.
  async getSelectProgress(progressId: string) {
    const body = {
      ...this.cachedAuth,
      method: 'get_select_progress',
      progress_id: progressId,
    };
    return WebTools.apiPost<{
      status: 'pending' | 'processing' | 'complete';
      phase: string;
      done: number;
      total: number;
    }>(ImageSyncService.LAMBDA_URL, body);
  }
}


function buildAWSUploadForm(presignedInfo: S3PresignedUploadInfo,
  filename: string,
  subdir: string,
  sha1: string,
  png: ArrayBuffer)
{
  const form = new FormData();
  for (const k in presignedInfo.fields) {
    if (k === 'key') continue;
    form.append(k, presignedInfo.fields[k]);
  }
  form.append('Content-Type', 'image/png');
  form.append('x-amz-meta-sha1', sha1);
  form.append('key', presignedInfo.fields.key.replace('${filename}', subdir + '/' + filename + '.png'));
  form.append('file', new Blob([png], { type: 'image/png' }), filename);
  return form;
}


interface ImageBasicProps {
  type: string;
  eye: string;
  filepath: string;
}

type ImageTree<T> = {
  [key: string]: {
    OD: T;
    OS: T;
  }
}

type VividSyncResponse = {
  delete: string | null;
  rows: clinic_user_retinal_images[];
}

type VividUploadResponse = {
  write: string[];
  skip: string[];
  error: string[];
}

type VividDeleteResponse = {
  paths: string[];
}

type SubdirResponse = {
  subdir_info: ImageTree<string>;
}

type S3PresignedGetInfo = {
  [key:string]: string
};

type S3PresignedUploadInfo = {
  url: string;
  fields: {
    key: string;
    AWSAccessKeyId: string;
    'x-amz-security-token': string;
    policy: string;
    signature: string;
  }
};

type PresignedUploadResponse = {
  presigned_info: S3PresignedUploadInfo;
}

type E2EUploadUrlResponse = {
  status: 'upload' | 'exists';
  finalFilename: string;
  destPath: string;
  presigned_info?: S3PresignedUploadInfo;
}

type S3DeleteResponse = {
  errors: any[];
}

interface VividAuth {
  u: string;
  p: string;
}

interface VividAuthWithPatient extends VividAuth {
  patient_id: number;
}

export {
  ImageSyncService,
  type ImageBasicProps,
  type ImageTree,
  type S3PresignedGetInfo,
  type S3PresignedUploadInfo,
  buildAWSUploadForm,
};
